/**
 * DM Invitations Context
 *
 * Polls for pending workspace invitations and provides accept/decline actions.
 * The badge count drives the notification indicator in the shell header.
 *
 * Also tracks pending GROUP workspace invitations (separate from DM invitations)
 * so the Settings button can show a badge when workspace invitations are pending.
 *
 * Polling strategy:
 * - Tab visible: poll every 10s (faster UX — user is actively waiting)
 * - Tab hidden:  pause polling (saves battery and bandwidth)
 * - Tab re-focused: immediately refresh so user sees fresh state
 */

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import * as workspaceClient from '@yada/api-client';
import type { Workspace } from '@yada/api-client';

import { getProfilesForCurrentAccount } from '../accounts/accountService';

export interface DmInvitation {
  workspaceId: string;
  workspaceName: string;
  /** Profile ID of the person who initiated the DM */
  initiatorProfileId: string;
  initiatorName: string;
  createdAt: string;
}

interface DmInvitationsContextValue {
  pendingInvitations: DmInvitation[];
  badgeCount: number;
  /** Count of pending GROUP workspace invitations (not DMs). Used to badge the Settings button. */
  pendingGroupInvitationCount: number;
  accept(workspaceId: string): Promise<void>;
  decline(workspaceId: string): Promise<void>;
  refresh(): Promise<void>;
}

const DmInvitationsContext = createContext<DmInvitationsContextValue>({
  pendingInvitations: [],
  badgeCount: 0,
  pendingGroupInvitationCount: 0,
  accept: async () => {},
  decline: async () => {},
  refresh: async () => {},
});

export function useDmInvitations() {
  return useContext(DmInvitationsContext);
}

/**
 * Extract the "other party" profile ID from a DM workspace.
 * DM workspace IDs have the form `dm:{profileA}:{profileB}` (sorted).
 */
function getInitiatorFromWorkspace(workspace: Workspace, myProfileIds: string[]): string {
  // For DM workspaces: pick the profile ID that isn't ours
  if (workspace.id.startsWith('dm:')) {
    const withoutPrefix = workspace.id.slice(3); // Remove 'dm:'
    const midpoint = withoutPrefix.indexOf(':', 36); // UUID is 36 chars
    if (midpoint > 0) {
      const idA = withoutPrefix.slice(0, midpoint);
      const idB = withoutPrefix.slice(midpoint + 1);
      const isMineA = myProfileIds.includes(idA);
      const isMineB = myProfileIds.includes(idB);
      if (isMineA && !isMineB) return idB;
      if (isMineB && !isMineA) return idA;
      // Fallback
      return idA === myProfileIds[0] ? idB : idA;
    }
  }
  return 'unknown';
}

/** Poll every 30s when tab is active; pause when hidden to save resources */
const POLL_INTERVAL_VISIBLE_MS = 30_000;

export function DmInvitationsProvider({ children }: { children: React.ReactNode }) {
  const [pendingInvitations, setPendingInvitations] = useState<DmInvitation[]>([]);
  const [pendingGroupInvitationCount, setPendingGroupInvitationCount] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = useCallback(async () => {
    const accountStr = localStorage.getItem('yada:account:account');
    if (accountStr) {
      try {
        const account = JSON.parse(accountStr);
        if (account.isAccountless || account.isAccountless === 'true') {
          return; // Skip for accountless
        }
      } catch {
        // ignore
      }
    }

    const currentProfileId = localStorage.getItem('yada:account:activeProfileId');
    if (!currentProfileId) return;

    try {
      const [workspaces, allProfiles] = await Promise.all([
        workspaceClient.listWorkspaces(),
        getProfilesForCurrentAccount(),
      ]);

      const myProfileIds = allProfiles.map((p) => p.id);
      if (!myProfileIds.includes(currentProfileId)) {
        myProfileIds.push(currentProfileId);
      }

      // Track pending group invitations for Settings badge
      const groupInvitationCount = workspaces.filter(
        (w) => w.membership_status === 'invited' && w.workspace_type === 'group'
      ).length;
      setPendingGroupInvitationCount(groupInvitationCount);

      const invitations: DmInvitation[] = workspaces
        .filter((w) => w.membership_status === 'invited' && w.workspace_type === 'dm')
        .map((w) => {
          const initiatorProfileId = getInitiatorFromWorkspace(w, myProfileIds);
          return {
            workspaceId: w.id,
            workspaceName: w.name,
            initiatorProfileId,
            initiatorName: 'Someone',
            createdAt: w.created_at,
          };
        });

      if (invitations.length > 0) {
        const { resolveProfile } = await import('@yada/storage');
        const resolvedInvitations = await Promise.all(
          invitations.map(async (inv) => {
            if (inv.initiatorProfileId === 'unknown') return inv;
            try {
              const profile = await resolveProfile(inv.initiatorProfileId);
              if (profile?.name) {
                return { ...inv, initiatorName: profile.name };
              }
            } catch {
              // ignore — fallback to placeholder
            }
            return inv;
          })
        );
        setPendingInvitations(resolvedInvitations);
      } else {
        setPendingInvitations(invitations);
      }
    } catch (err) {
      // Not authenticated yet or network error — silently ignore
      console.debug('[DmInvitationsContext] Failed to fetch invitations:', err);
    }
  }, []);

  /** Start polling interval */
  const startPolling = useCallback(() => {
    if (intervalRef.current) return; // Already polling
    intervalRef.current = setInterval(refresh, POLL_INTERVAL_VISIBLE_MS);
  }, [refresh]);

  /** Stop polling interval */
  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Initial fetch + visibility-aware polling
  useEffect(() => {
    // Fetch immediately on mount
    refresh();

    // Start polling if tab is already visible
    if (!document.hidden) {
      startPolling();
    }

    // Pause/resume polling based on tab visibility to save battery and bandwidth
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        // Tab became visible — refresh immediately, then resume polling
        refresh();
        startPolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refresh, startPolling, stopPolling]);

  const accept = useCallback(async (workspaceId: string) => {
    await workspaceClient.acceptWorkspace(workspaceId);
    // Remove from pending list immediately (optimistic)
    setPendingInvitations((prev) => prev.filter((inv) => inv.workspaceId !== workspaceId));
    // Notify the chat app so it can sync the new conversation into its store
    window.dispatchEvent(new CustomEvent('yada:dm-accepted', { detail: { workspaceId } }));
  }, []);

  const decline = useCallback(async (workspaceId: string) => {
    await workspaceClient.declineWorkspace(workspaceId);
    setPendingInvitations((prev) => prev.filter((inv) => inv.workspaceId !== workspaceId));
  }, []);

  return (
    <DmInvitationsContext.Provider
      value={{
        pendingInvitations,
        badgeCount: pendingInvitations.length,
        pendingGroupInvitationCount,
        accept,
        decline,
        refresh,
      }}
    >
      {children}
    </DmInvitationsContext.Provider>
  );
}
